import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {MasterDataTypes} from '@evencel/api';
import {
    DEFUALT_SEARCH,
    IActivitySubType,
    IMasterData,
    IMasterDataActivate,
    IMasterDataCreate,
    IMasterDataSelectItem,
    IPagedData,
    IPagedSearch,
    ISearch,
    ISearchDispositions,
    ISelectItem,
    mongoQueryParser,
} from '@evencel/models';
import {Observable, map} from 'rxjs';

export const MASTER_DATA_URL = 'master-data';

@Injectable({providedIn: 'root'})
export class MasterDataService {
    httpClient = inject(HttpClient);

    post(data: IMasterDataCreate): Observable<IMasterData> {
        return this.httpClient.post<IMasterData>(MASTER_DATA_URL, data);
    }

    patch(data: IMasterData): Observable<IMasterData> {
        const url = `${MASTER_DATA_URL}/${data._id}`;
        return this.httpClient.patch<IMasterData>(url, data);
    }

    activate(data: IMasterDataActivate): Observable<IMasterData> {
        const {active} = data;
        const url = `${MASTER_DATA_URL}/${data._id}/activate`;
        return this.httpClient.patch<IMasterData>(url, {active});
    }

    delete(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${MASTER_DATA_URL}/${id}`);
    }

    restore(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${MASTER_DATA_URL}/${id}/restore`);
    }

    createMasterDataValue(masterDataId: string, value: ISelectItem) {
        const url = `${MASTER_DATA_URL}/${masterDataId}`;
        return this.httpClient.post<IMasterData>(url, value);
    }

    updateMasterDataValue(masterDataId: string, value: ISelectItem) {
        const valueId = value._id;
        // delete value._id;
        const url = `${MASTER_DATA_URL}/${masterDataId}/${valueId}`;
        return this.httpClient.patch<IMasterData>(url, value);
    }

    deleteMasterDataValue(masterDataId: string, value: ISelectItem) {
        const valueId = value._id;
        // delete value._id;
        const url = `${MASTER_DATA_URL}/${masterDataId}/${valueId}`;
        return this.httpClient.delete<IMasterData>(url);
    }

    private searchParams(data: IPagedSearch) {
        const {active, direction} = data.sort;
        const {pageIndex, pageSize} = data.page;
        let params = new HttpParams();
        params = params.append('pageIndex', `${pageIndex}`);
        params = params.append('pageSize', `${pageSize}`);
        params = params.append('sort', active);
        params = params.append('order', direction);
        params = params.append('projection', data.projection || 'dataTable');
        return params;
    }

    search(data: IPagedSearch): Observable<IPagedData<IMasterData>> {
        const params = this.searchParams(data);
        return this.httpClient.post<IPagedData<IMasterData>>(`${MASTER_DATA_URL}/search`, data.condition, {params});
    }

    searchIn(data: IPagedSearch): Observable<Record<string, unknown>> {
        const params = this.searchParams(data);
        return this.httpClient.post<Record<string, unknown>>(`${MASTER_DATA_URL}/search/in`, data.condition, {
            params,
        });
    }

    seachDispostions(req: ISearchDispositions) {
        return this.httpClient.post<IActivitySubType>(`${MASTER_DATA_URL}/search/dispositions`, req);
    }

    seachDisputeType() {
        return this.httpClient.post<ISelectItem[]>(`${MASTER_DATA_URL}/search/dispute-type`, null);
    }

    searchOne<T>(data: ISearch): Observable<T> {
        let params = new HttpParams();
        params = params.append('projection', data.projection || 'search');
        return this.httpClient.post<T>(`${MASTER_DATA_URL}/search/one`, data.condition, {params});
    }

    searchByType<T = ISelectItem>(type: MasterDataTypes, projection?: string) {
        const condition = mongoQueryParser.parse({type: {$eq: type}});
        return this.searchOne<IMasterDataSelectItem<T>>({
            ...DEFUALT_SEARCH,
            condition,
            projection,
        }).pipe(map(d => d.value || []));
    }

    searchById<T = ISelectItem>(id: string, projection?: string) {
        const condition = mongoQueryParser.parse({_id: {$eqo: id}});
        return this.searchOne<IMasterDataSelectItem<T>>({
            ...DEFUALT_SEARCH,
            condition,
            projection,
        }).pipe(
            map(d => {
                if (d !== null) {
                    return d.value;
                } else {
                    return [];
                }
            })
        );
    }

    searchInByType(types: string[]) {
        const condition = mongoQueryParser.parse({type: {$in: types}});
        return this.searchIn({...DEFUALT_SEARCH, condition});
    }

    searchByCategory(category: string) {
        const condition = mongoQueryParser.parse({category: {$eq: category}});
        return this.searchIn({...DEFUALT_SEARCH, condition});
    }

    getOne(id: string): Observable<IMasterData> {
        let params = new HttpParams();
        params = params.append('projection', `form`);
        const url = `${MASTER_DATA_URL}/${id}`;
        return this.httpClient.get<IMasterData>(url, {params});
    }

    get(data: IPagedSearch): Observable<IPagedData<IMasterData>> {
        const params = this.searchParams(data);
        return this.httpClient.get<IPagedData<IMasterData>>(MASTER_DATA_URL, {
            params,
        });
    }
}
